/**
 * Поступления КС-3: проверка формы нового плана и редактора факта по строке, данные для
 * сохранения, «100 % КС-3» от суммы в редакторе, период БДДС по дате оплаты и состояние
 * редакторов строк (ожидание, ошибка и закрытие — у своей строки).
 *
 * Суммы разбирает `parseMoney` из shared/lib/money — его передают параметром: модуль без
 * импортов значений, проверяется node:test (`model.test.mjs`).
 */

export type ParseMoney = (raw: string) => number | null;

const round2 = (n: number) => Math.round(n * 100) / 100;

const NOT_A_NUMBER = 'Сумма — числом, например 1 250 000,50';
const NEGATIVE = 'Сумма не может быть отрицательной';

/** Сумма поля: `empty` — что сказать о пустом поле (`null` — пусто значит 0). */
function amount(
  raw: string,
  parse: ParseMoney,
  empty: string | null,
): { value: number; error?: string } {
  const s = raw.trim();
  if (s === '') return empty === null ? { value: 0 } : { value: 0, error: empty };
  if (/^[-−–]/.test(s)) return { value: 0, error: NEGATIVE };
  const v = parse(s);
  return v === null ? { value: 0, error: NOT_A_NUMBER } : { value: v };
}

// ── КС-3 к оплате ────────────────────────────────────────────────────────

/**
 * К оплате по КС-3: подписано минус зачёт аванса и гарантийное удержание, не меньше нуля —
 * так считает сервер (`ceo_inflow_plans.expected_net_inflow`).
 */
export function ks3NetToPay(ks3: number, advance: number, retention: number): number {
  return round2(Math.max(0, ks3 - advance - retention));
}

/**
 * «100 % КС-3» в редакторе строки: от суммы КС-3, введённой сейчас, а не сохранённой.
 * `null` — суммы КС-3 в редакторе нет или это не число.
 */
export function fullKs3Payment(
  ks3Raw: string,
  advance: number,
  retention: number,
  parse: ParseMoney,
): { ks3: number; net: number } | null {
  const { value, error } = amount(ks3Raw, parse, '');
  if (error || value <= 0) return null;
  return { ks3: value, net: ks3NetToPay(value, advance, retention) };
}

// ── Период БДДС ──────────────────────────────────────────────────────────

/** Первое число месяца даты «ГГГГ-ММ-ДД»; не дата — `undefined`. */
export function periodOf(isoDate: string): string | undefined {
  return /^\d{4}-\d{2}-\d{2}/.test(isoDate) ? `${isoDate.slice(0, 7)}-01` : undefined;
}

/**
 * Дата оплаты по умолчанию для новой строки: сегодня, а если в фильтре выбран другой
 * месяц — его первое число. Фильтр только подсказывает: период строки — месяц даты оплаты.
 */
export function defaultExpectedDate(periodFilter: string, today: string): string {
  const filterPeriod = periodOf(periodFilter);
  if (!filterPeriod || filterPeriod === periodOf(today)) return today;
  return filterPeriod;
}

/** Периоды фильтра: от 6 месяцев назад до 3 вперёд по местной дате. */
export function recentPeriods(now: Date): string[] {
  const out: string[] = [];
  for (let offset = -6; offset <= 3; offset += 1) {
    const d = new Date(now.getFullYear(), now.getMonth() + offset, 1);
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`);
  }
  return out;
}

// ── Новая строка плана ───────────────────────────────────────────────────

export interface PlanForm {
  projectName: string;
  customerName: string;
  contractNo: string;
  expectedDate: string;
  bdds: string;
  ks3: string;
  ks3DocNo: string;
  advance: string;
  retention: string;
  actual: string;
  responsible: string;
}

export type PlanField = keyof PlanForm;
export type PlanErrors = Partial<Record<PlanField, string>>;

export interface PlanPayload {
  period_month?: string;
  expected_payment_date?: string;
  project_name: string;
  customer_name: string;
  contract_number: string;
  bdds_planned_amount: number;
  ks2_submitted_amount: number;
  ks3_signed_amount: number;
  ks3_doc_number?: string;
  advance_deduction: number;
  retention_deduction: number;
  actual_received_amount: number;
  responsible_person?: string;
}

/**
 * Проверка формы нового плана и данные для сервера. План БДДС обязателен (0 — поступление
 * вне плана); КС-3, удержания и факт можно оставить пустыми — это «нет суммы». Период —
 * месяц даты оплаты. План не создаёт подачи КС-2: она равна подписанной КС-3 или нулю.
 */
export function checkPlanForm(
  form: PlanForm,
  parse: ParseMoney,
): { errors: PlanErrors; payload: PlanPayload | null } {
  const errors: PlanErrors = {};
  if (!form.projectName.trim()) errors.projectName = 'Укажите строительный объект';
  if (!form.customerName.trim()) errors.customerName = 'Укажите заказчика';

  const read = (field: PlanField, empty: string | null) => {
    const r = amount(form[field], parse, empty);
    if (r.error) errors[field] = r.error;
    return r.value;
  };
  const bdds = read('bdds', 'Укажите сумму плана; 0 — если поступление вне плана');
  const ks3 = read('ks3', null);
  const advance = read('advance', null);
  const retention = read('retention', null);
  const actual = read('actual', null);

  if (!errors.bdds && !errors.ks3 && bdds <= 0 && ks3 <= 0) {
    errors.bdds = 'Укажите сумму плана БДДС или подписанной КС-3';
  }
  if (!errors.ks3 && !errors.advance && !errors.retention && advance + retention > 0) {
    const field: PlanField = advance > 0 ? 'advance' : 'retention';
    if (ks3 <= 0) {
      errors[field] = 'Аванс и ГУ удерживают из КС-3 — укажите сумму КС-3';
    } else if (advance + retention > ks3 + 0.005) {
      errors[field] = 'Удержания больше суммы КС-3';
    }
  }
  if (Object.keys(errors).length > 0) return { errors, payload: null };

  const date = form.expectedDate.trim();
  return {
    errors,
    payload: {
      period_month: periodOf(date),
      expected_payment_date: date || undefined,
      project_name: form.projectName.trim(),
      customer_name: form.customerName.trim(),
      contract_number: form.contractNo.trim(),
      bdds_planned_amount: bdds,
      ks2_submitted_amount: ks3 > 0 ? ks3 : 0,
      ks3_signed_amount: ks3,
      ks3_doc_number: form.ks3DocNo.trim() || undefined,
      advance_deduction: advance,
      retention_deduction: retention,
      actual_received_amount: actual,
      responsible_person: form.responsible.trim() || undefined,
    },
  };
}

// ── Редактор факта по строке ─────────────────────────────────────────────

export interface FactDraft {
  ks3: string;
  ks3DocNo: string;
  actual: string;
}

export type FactField = keyof FactDraft;
export type FactErrors = Partial<Record<FactField, string>>;

/** Что сохранено в строке сейчас. */
export interface FactCurrent {
  ks3_signed_amount: number;
  ks3_doc_number: string | null;
  actual_received_amount: number;
}

export interface FactPayload {
  ks3_signed_amount?: number;
  ks3_doc_number?: string;
  actual_received_amount?: number;
}

/**
 * Проверка редактора факта. Пустая сумма — ошибка, а не «не менять»: поле открывается с
 * сохранённой суммой, и стёртая сумма — это либо ноль, либо опечатка. Ноль записывается
 * нулём. Передаётся только изменённое: не переданное сервер не трогает.
 */
export function checkFactDraft(
  draft: FactDraft,
  current: FactCurrent,
  parse: ParseMoney,
): { errors: FactErrors; payload: FactPayload | null } {
  const errors: FactErrors = {};
  const ks3 = amount(draft.ks3, parse, 'Укажите сумму КС-3; 0 — если КС-3 не подписана');
  const actual = amount(draft.actual, parse, 'Укажите, сколько поступило; 0 — если денег не было');
  if (ks3.error) errors.ks3 = ks3.error;
  if (actual.error) errors.actual = actual.error;
  if (Object.keys(errors).length > 0) return { errors, payload: null };

  const payload: FactPayload = {};
  if (Math.abs(ks3.value - current.ks3_signed_amount) >= 0.005) {
    payload.ks3_signed_amount = ks3.value;
  }
  const docNo = draft.ks3DocNo.trim();
  if (docNo !== (current.ks3_doc_number ?? '').trim()) payload.ks3_doc_number = docNo;
  if (Math.abs(actual.value - current.actual_received_amount) >= 0.005) {
    payload.actual_received_amount = actual.value;
  }
  return { errors, payload };
}

/** Редактор строки: черновик, ожидание ответа, ошибки полей и ошибка сохранения. */
export interface RowEditor {
  draft: FactDraft;
  saving: boolean;
  fieldErrors: FactErrors;
  error: string | null;
}

export type RowEditors = Readonly<Record<string, RowEditor>>;

export function openEditor(editors: RowEditors, id: string, draft: FactDraft): RowEditors {
  if (editors[id]) return editors;
  return { ...editors, [id]: { draft, saving: false, fieldErrors: {}, error: null } };
}

export function editDraft(editors: RowEditors, id: string, patch: Partial<FactDraft>): RowEditors {
  const e = editors[id];
  if (!e || e.saving) return editors;
  const fieldErrors = { ...e.fieldErrors };
  for (const k of Object.keys(patch) as FactField[]) delete fieldErrors[k];
  return { ...editors, [id]: { ...e, draft: { ...e.draft, ...patch }, fieldErrors } };
}

/** Закрыть редактор строки; пока ждём ответа — нельзя. */
export function closeEditor(editors: RowEditors, id: string): RowEditors {
  if (!editors[id] || editors[id].saving) return editors;
  const next = { ...editors };
  delete next[id];
  return next;
}

export function showFieldErrors(editors: RowEditors, id: string, fieldErrors: FactErrors): RowEditors {
  const e = editors[id];
  return e ? { ...editors, [id]: { ...e, fieldErrors, error: null } } : editors;
}

/** Можно ли отправить строку: редактор открыт и ответа на прошлую отправку не ждём. */
export function canSave(editors: RowEditors, id: string): boolean {
  return Boolean(editors[id]) && !editors[id].saving;
}

export function startSaving(editors: RowEditors, id: string): RowEditors {
  const e = editors[id];
  return e ? { ...editors, [id]: { ...e, saving: true, error: null } } : editors;
}

/**
 * Ответ сервера по строке `id`: успех закрывает только её редактор, ошибка остаётся у неё
 * с введёнными значениями. Редакторы других строк не трогаются.
 */
export function finishSaving(editors: RowEditors, id: string, error: string | null): RowEditors {
  const e = editors[id];
  if (!e) return editors;
  if (error === null) {
    const next = { ...editors };
    delete next[id];
    return next;
  }
  return { ...editors, [id]: { ...e, saving: false, error } };
}
